import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { formatAllergens } from "../lib/allergens";

const CATEGORIES = ["Breakfast", "Lunch", "Dinner", "Drinks", "Alcohol"];

// Subgroup ordering per category (subgroups not listed appear at the end)
const SUBGROUP_ORDER = {
  Breakfast: ["Irish Breakfast", "On Sourdough", "Pancakes or Brioche French Toast", "Cereals", "Add Ons"],
  Lunch: ["Salads", "Main Dishes", "Sandwiches and Wraps", "Sides"],
  Dinner: ["Appetizers", "Rice Bowls", "Noodles", "Chef Specialities", "Sides"],
  Drinks: ["Coffee", "Tea", "Smoothies", "Milkshakes", "Soft Drinks"],
  Alcohol: ["Red Wine", "White Wine", "Rosé", "Prosecco"],
};

function MenuRow({ item }) {
  return (
    <div className="break-inside-avoid" data-testid={`menu-item-${item.id}`}>
      <div className="flex items-end mb-1">
        <h4 className="font-serif-display text-xl sm:text-2xl text-[#5979A9] leading-tight">{item.name}</h4>
        {item.price ? (
          <>
            <span className="dotted-leader" aria-hidden="true" />
            <span className="font-semibold text-lg text-[#E05947] whitespace-nowrap">{item.price}</span>
          </>
        ) : null}
      </div>
      {item.description && (
        <p className="text-sm sm:text-[15px] text-[#4A5568] leading-snug pr-2 sm:pr-12">{item.description}</p>
      )}
      {item.allergens && item.allergens.length > 0 && (
        <p className="text-[11px] text-[#E05947]/80 italic mt-1.5 tracking-wide">{formatAllergens(item.allergens)}</p>
      )}
    </div>
  );
}

function SubgroupTitle({ title }) {
  const slug = title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  return (
    <div className="col-span-full mt-4 mb-1" data-testid={`menu-subgroup-${slug}`}>
      <div className="flex items-center gap-4">
        <span className="text-[11px] uppercase tracking-[0.32em] font-bold text-[#E05947]">{title}</span>
        <span className="flex-1 h-px bg-[#5979A9]/15"/>
      </div>
    </div>
  );
}

export default function Menu() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("Breakfast");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/menu")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    const visible = items
      .filter((i) => i.category === active)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const order = SUBGROUP_ORDER[active] || [];
    const map = new Map();
    order.forEach((sg) => map.set(sg, []));
    visible.forEach((it) => {
      const sg = it.subgroup || "Other";
      if (!map.has(sg)) map.set(sg, []);
      map.get(sg).push(it);
    });
    return Array.from(map.entries()).filter(([, list]) => list.length > 0);
  }, [items, active]);

  return (
    <section id="menu" data-testid="menu-section" className="py-24 sm:py-32" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FAF4F1 100%)" }}>
      <div className="container-wide">
        <div className="reveal text-center max-w-2xl mx-auto mb-14">
          <div className="eyebrow mb-3">Our Kitchen</div>
          <h2 className="section-title text-4xl sm:text-5xl lg:text-6xl">The Menu</h2>
          <p className="mt-5 text-[#4A5568] text-lg leading-relaxed">
            Fresh, made-to-order, with care. From Donegal mornings to Himalayan evenings.
          </p>
        </div>

        <div className="flex justify-center gap-2 sm:gap-6 overflow-x-auto mb-12 pb-2 -mx-4 px-4" data-testid="menu-tabs">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActive(cat)}
              data-testid={`menu-tab-${cat.toLowerCase()}`}
              className={`shrink-0 px-4 sm:px-5 pb-3 text-sm sm:text-base tracking-wide border-b-2 transition-colors duration-300 ${
                active === cat
                  ? "border-[#E05947] text-[#E05947] font-semibold"
                  : "border-transparent text-[#5979A9] hover:text-[#E05947]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-[#5979A9]/60">Loading menu…</p>
        ) : groups.length === 0 ? (
          <p className="text-center text-[#5979A9]/60">No items yet in this category.</p>
        ) : (
          <div data-testid="menu-grid">
            {groups.map(([sg, list]) => (
              <div key={sg} className="mb-10">
                {sg !== "Other" && <SubgroupTitle title={sg} />}
                <div className="grid lg:grid-cols-2 gap-x-16 gap-y-8 mt-5">
                  {list.map((it) => <MenuRow key={it.id} item={it} />)}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-[#5979A9]/70 mt-10 italic max-w-2xl mx-auto">
          Allergens are numbered (1 Cereals, 2 Crustaceans, 3 Eggs, 4 Fish, 5 Peanuts, 6 Soybeans, 7 Milk, 8 Nuts, 9 Celery, 10 Mustard, 11 Sesame, 12 Sulphites). Please let our team know about any allergies.
        </p>
      </div>
    </section>
  );
}
